import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Home, Mail, CheckSquare, FileText, LogOut, Send, Settings as SettingsIcon, BarChart3, ChevronDown, ChevronRight, HelpCircle, Bell } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ReviewQueue from './pages/ReviewQueue';
import EmailDetail from './pages/EmailDetail';
import ApprovedQueue from './pages/ApprovedQueue';
import Settings from './pages/Settings';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function Sidebar() {
  const location = useLocation();
  const { logout } = useAuth();
  const [ticketsExpanded, setTicketsExpanded] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Auto-collapse sidebar by default
  useEffect(() => {
    setSidebarCollapsed(true);
  }, [location.pathname]);

  const primaryNavItems = [
    { path: '/', icon: Home, label: 'Home', tooltip: 'Dashboard' },
    { path: '/emails', icon: Mail, label: 'Mailbox', tooltip: 'Mailbox' },
  ];

  const ticketSubItems = [
    { path: '/review', label: 'To-Do' },
    { path: '/approved', label: 'Approved' },
    { path: '/emails', label: 'All Emails' },
  ];

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  // Hover to expand on detail pages
  const shouldHoverExpand = location.pathname.startsWith('/review/') && location.pathname !== '/review';

  return (
    <>
      {/* Primary Sidebar - Dark Blue */}
      <div 
        className="w-12 bg-[#1e3a8a] flex flex-col items-center py-4 space-y-4"
        onMouseEnter={() => setSidebarCollapsed(false)}
      >
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="w-10 h-10 flex items-center justify-center rounded-lg text-blue-200 hover:bg-blue-700 hover:text-white transition-colors"
        >
          {sidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5 rotate-90" />}
        </button>
        
        {primaryNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              title={item.tooltip}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-blue-200 hover:bg-blue-700 hover:text-white'
              }`}
            >
              <Icon className="h-5 w-5" />
            </Link>
          );
        })}
        
        <div className="flex-1" />
        
        <Link
          to="/settings"
          title="Settings"
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
            location.pathname === '/settings'
              ? 'bg-blue-600 text-white'
              : 'text-blue-200 hover:bg-blue-700 hover:text-white'
          }`}
        >
          <SettingsIcon className="h-5 w-5" />
        </Link>
        
        <button
          onClick={handleLogout}
          title="Logout"
          className="w-10 h-10 flex items-center justify-center rounded-lg text-blue-200 hover:bg-blue-700 hover:text-white transition-colors"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      {/* Secondary Sidebar - Light Gray - Collapsible */}
      <div 
        className={`bg-gray-50 border-r border-gray-200 flex flex-col transition-all duration-300 ${
          sidebarCollapsed ? 'w-0 overflow-hidden' : 'w-64'
        }`}
        onMouseLeave={() => setSidebarCollapsed(true)}
      >
        <div className="w-64">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Mailbox</h2>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {/* Tickets Section */}
            <div className="py-2">
              <button
                onClick={() => setTicketsExpanded(!ticketsExpanded)}
                className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                <span className="flex items-center">
                  <CheckSquare className="h-4 w-4 mr-2" />
                  TICKETS
                </span>
                {ticketsExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              
              {ticketsExpanded && (
                <div className="mt-1">
                  {ticketSubItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center justify-between px-8 py-2 text-sm transition-colors ${
                          isActive
                            ? 'bg-blue-50 text-blue-600 font-medium border-r-2 border-blue-600'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Toaster position="top-right" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <div className="flex h-screen bg-white overflow-hidden">
                    <Sidebar />
                    <main className="flex-1 overflow-y-auto bg-gray-50">
                      <div className="h-full">
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/review" element={<ReviewQueue />} />
                          <Route path="/review/:id" element={<EmailDetail />} />
                          <Route path="/approved" element={<ApprovedQueue />} />
                          <Route path="/emails" element={<ReviewQueue showAll />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                      </div>
                    </main>
                  </div>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
